// The portable workflow validator (docs/13 "Free tools"): the document checks a reader can run
// in the browser before downloading or publishing a workflow. The graph rules are docs/12's
// ("Validation"), which OCS applies on save and publish; this is the community's copy of them, so
// a pass here is a strong sign, and OCS has the final word.
import { type Condition, type Graph, type GraphNode, PORTABLE_FORMAT, type PortableWorkflow } from "./portable";

export interface Issue {
  where: string;
  message: string;
}

const KINDS = new Set(["prompt", "approval", "orchestrate", "end"]);
const OUTCOMES = new Set(["succeeded", "failed", "timed_out", "skipped"]);

/** Reads the text as JSON; a syntax error is the only issue then. */
export function parseDocument(text: string): { doc?: PortableWorkflow; issues: Issue[] } {
  try {
    return { doc: JSON.parse(text) as PortableWorkflow, issues: [] };
  } catch (e) {
    return { issues: [{ where: "file", message: `Not valid JSON: ${(e as Error).message}` }] };
  }
}

function reachable(graph: Graph): Set<string> {
  const seen = new Set<string>();
  const queue = [graph.start];
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of graph.edges) if (e.from === id) queue.push(e.to);
  }
  return seen;
}

function conditionIssues(node: GraphNode, when: Condition, where: string): Issue[] {
  if (when === "otherwise") return [];
  if ("answer" in when) {
    if (node.kind !== "approval") return [{ where, message: "an answer condition must leave an approval step" }];
    return node.options.includes(when.answer) ? [] : [{ where, message: `“${when.answer}” is not one of the approval's options` }];
  }
  if ("outcome" in when) return OUTCOMES.has(when.outcome) ? [] : [{ where, message: `unknown outcome “${when.outcome}”` }];
  if ("output" in when) {
    const declared = node.kind === "prompt" ? (node.outputs ?? []) : [];
    return declared.includes(when.output.key) ? [] : [{ where, message: `output “${when.output.key}” is not declared by ${node.id}` }];
  }
  return [{ where, message: "unknown condition" }];
}

export function validateGraph(graph: Graph): Issue[] {
  const issues: Issue[] = [];
  const ids = new Map<string, GraphNode>();
  for (const n of graph.nodes ?? []) {
    if (ids.has(n.id)) issues.push({ where: `step ${n.id}`, message: "the id is used twice" });
    ids.set(n.id, n);
    if (!KINDS.has(n.kind)) issues.push({ where: `step ${n.id}`, message: `unknown kind “${String(n.kind)}”` });
    if (n.kind === "prompt" && !n.prompt?.trim()) issues.push({ where: `step ${n.id}`, message: "the prompt is empty" });
    if (n.kind === "approval" && (!n.question?.trim() || !n.options?.length)) issues.push({ where: `step ${n.id}`, message: "an approval needs a question and options" });
  }
  if (!ids.has(graph.start)) issues.push({ where: "graph", message: `the start “${graph.start}” is not a step` });
  for (const [i, e] of (graph.edges ?? []).entries()) {
    const where = `edge ${e.from} → ${e.to}`;
    const from = ids.get(e.from);
    if (!from) issues.push({ where, message: `“${e.from}” is not a step` });
    if (!ids.has(e.to)) issues.push({ where, message: `“${e.to}” is not a step` });
    if (from) issues.push(...conditionIssues(from, e.when, where));
    const later = graph.edges.slice(i + 1).some((x) => x.from === e.from);
    if (e.when === "otherwise" && later) issues.push({ where, message: "“otherwise” must be the step's last way out" });
  }
  for (const n of ids.values()) {
    if (n.kind !== "end" && !graph.edges.some((e) => e.from === n.id)) issues.push({ where: `step ${n.id}`, message: "has no way out" });
  }
  if (ids.has(graph.start)) {
    const seen = reachable(graph);
    for (const id of ids.keys()) if (!seen.has(id)) issues.push({ where: `step ${id}`, message: "cannot be reached from the start" });
    if (![...seen].some((id) => ids.get(id)?.kind === "end")) issues.push({ where: "graph", message: "no end can be reached" });
  }
  return issues;
}

export function validateDocument(doc: PortableWorkflow): Issue[] {
  const issues: Issue[] = [];
  if (doc.format !== PORTABLE_FORMAT) issues.push({ where: "format", message: `expected “${PORTABLE_FORMAT}”` });
  if (!doc.workflow?.slug || !doc.workflow?.name) issues.push({ where: "workflow", message: "needs a slug and a name" });
  const graph = doc.version?.definition;
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) {
    return [...issues, { where: "version.definition", message: "needs start, nodes and edges" }];
  }
  issues.push(...validateGraph(graph));
  const named = new Set((doc.version.requirements ?? []).flatMap((r) => (r.kind === "credential" && r.name ? [r.name] : [])));
  for (const n of graph.nodes) {
    if (n.kind !== "prompt") continue;
    for (const g of n.pre_execution?.broker ?? []) {
      if (!named.has(g.requirement)) issues.push({ where: `step ${n.id}`, message: `its guard uses requirement “${g.requirement}”, which the version does not name` });
    }
  }
  const skills = new Set((doc.skills ?? []).map((s) => s.slug));
  for (const s of doc.version.skills ?? []) if (!skills.has(s)) issues.push({ where: "version.skills", message: `skill “${s}” is not in the file` });
  return issues;
}
